import { DRAWS, type DrawRow } from '../data/lotteryData';
import type { GameDrawResponse, GameInfoResponse } from '../api/types';

export type MatchOutcome =
  | 'jackpot'
  | 'won-small'
  | 'lost'
  | 'pending'
  | 'unknown-draw';

export interface TicketForMatch {
  main: number[];
  bonus: number[];
  drawDate?: string;
}

export interface MatchResult {
  outcome: MatchOutcome;
  mainHits: number;
  bonusHit: boolean;
  prize: number;
  matchedMain: number[];
  matchedBonus: number[];
  draw?: DrawRow;
}

/**
 * Normalise a user-supplied or model-extracted draw date to ISO `YYYY-MM-DD`.
 * Returns empty string if unparseable.
 */
export function normalizeDrawDate(input?: string): string {
  if (!input) return '';
  const trimmed = input.trim();
  if (!trimmed) return '';

  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (iso) {
    const normalized = `${iso[1]}-${iso[2]}-${iso[3]}`;
    return isValidIsoDate(normalized) ? normalized : '';
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return '';
  const y = parsed.getFullYear().toString().padStart(4, '0');
  const m = (parsed.getMonth() + 1).toString().padStart(2, '0');
  const d = parsed.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isValidIsoDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false;
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function drawIsoDate(draw: DrawRow): string {
  return normalizeDrawDate(draw.date);
}

/**
 * Match a ticket against the known set of draws. If `ticket.drawDate` is in the future
 * (or doesn't match any draw we have yet), we return a `pending` or `unknown-draw`
 * outcome so the UI can explain rather than lying to the user.
 */
export function matchAgainstDraw(
  ticket: TicketForMatch,
  draws: DrawRow[] = DRAWS,
  now = new Date()
): MatchResult {
  const wantedIso = normalizeDrawDate(ticket.drawDate);
  const todayIso = formatLocalYmd(now);

  if (!wantedIso) {
    return emptyMatch('unknown-draw');
  }

  const draw = draws.find((d) => drawIsoDate(d) === wantedIso);
  if (!draw) {
    if (wantedIso >= todayIso) {
      return emptyMatch('pending');
    }
    return emptyMatch('unknown-draw');
  }

  const mainHits = ticket.main.filter((n) => draw.main.includes(n)).length;
  const matchedMain = ticket.main.filter((n) => draw.main.includes(n));
  const bonusHit =
    ticket.bonus.length > 0 && draw.bonus.some((n) => ticket.bonus.includes(n));
  const matchedBonus = ticket.bonus.filter((n) => draw.bonus.includes(n));

  const prize = computePrize(mainHits, bonusHit, draw);
  const outcome = outcomeFromPrize(prize, mainHits, bonusHit, ticket.main.length);

  return { outcome, mainHits, bonusHit, prize, matchedMain, matchedBonus, draw };
}

/**
 * Match against API-backed game draws. This intentionally avoids the old mock prize table:
 * official draw history lets us identify jackpot / lower-tier win / loss, but the API does
 * not currently return exact lower-tier prize amounts.
 */
export function matchAgainstGameInfo(
  ticket: TicketForMatch,
  gameInfo: GameInfoResponse | null | undefined,
  now = new Date()
): MatchResult {
  const wantedIso = normalizeDrawDate(ticket.drawDate);
  const todayIso = formatLocalYmd(now);

  if (!wantedIso) return emptyMatch('unknown-draw');
  if (!gameInfo) return wantedIso >= todayIso ? emptyMatch('pending') : emptyMatch('unknown-draw');

  const draw = gameInfo.draws.find((d) => normalizeDrawDate(d.iso) === wantedIso);
  if (!draw) {
    if (wantedIso >= todayIso) return emptyMatch('pending');
    return emptyMatch('unknown-draw');
  }

  return matchAgainstApiDraw(ticket, gameInfo.game, draw);
}

/**
 * Match against one exact API-backed draw lookup. This is the scanner path: a ticket can be
 * older than the home screen's recent-history cache, so the app asks the API for that date.
 */
export function matchAgainstGameDraw(
  ticket: TicketForMatch,
  gameDraw: GameDrawResponse | null | undefined,
  now = new Date()
): MatchResult {
  const wantedIso = normalizeDrawDate(ticket.drawDate);
  const todayIso = formatLocalYmd(now);

  if (!wantedIso) return emptyMatch('unknown-draw');
  if (!gameDraw?.draw) return wantedIso >= todayIso ? emptyMatch('pending') : emptyMatch('unknown-draw');
  if (normalizeDrawDate(gameDraw.draw.iso) !== wantedIso) {
    return wantedIso >= todayIso ? emptyMatch('pending') : emptyMatch('unknown-draw');
  }

  return matchAgainstApiDraw(ticket, gameDraw.game, gameDraw.draw);
}

function matchAgainstApiDraw(
  ticket: TicketForMatch,
  game: GameInfoResponse['game'],
  draw: GameInfoResponse['draws'][number]
): MatchResult {
  const mainHits = ticket.main.filter((n) => draw.main.includes(n)).length;
  const matchedMain = ticket.main.filter((n) => draw.main.includes(n));
  const bonusHit =
    game.bonusCount > 0 &&
    ticket.bonus.length > 0 &&
    draw.bonus.some((n) => ticket.bonus.includes(n));
  const matchedBonus = ticket.bonus.filter((n) => draw.bonus.includes(n));
  const jackpot = mainHits === game.mainCount && (game.bonusCount === 0 || bonusHit);
  const wonLowerTier = mainHits === game.mainCount || mainHits >= 3 || bonusHit;
  return {
    outcome: jackpot ? 'jackpot' : wonLowerTier ? 'won-small' : 'lost',
    mainHits,
    bonusHit,
    prize: jackpot ? draw.jackpot : 0,
    matchedMain,
    matchedBonus,
    draw: {
      date: draw.iso,
      main: draw.main,
      bonus: draw.bonus,
      jackpot: draw.jackpot,
      winners: 0,
    },
  };
}

function emptyMatch(outcome: Extract<MatchOutcome, 'pending' | 'unknown-draw'>): MatchResult {
  return { outcome, mainHits: 0, bonusHit: false, prize: 0, matchedMain: [], matchedBonus: [] };
}

function formatLocalYmd(date: Date): string {
  const y = date.getFullYear().toString().padStart(4, '0');
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const d = date.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function computePrize(mainHits: number, bonusHit: boolean, draw: DrawRow): number {
  // Simple, illustrative prize table for the mock Chancey Millions game.
  if (mainHits === 5 && bonusHit) return draw.jackpot;
  if (mainHits === 5) return 1_000_000;
  if (mainHits === 4 && bonusHit) return 50_000;
  if (mainHits === 4) return 500;
  if (mainHits === 3 && bonusHit) return 100;
  if (mainHits === 3) return 10;
  if (mainHits === 2 && bonusHit) return 5;
  if (bonusHit) return 2;
  return 0;
}

function outcomeFromPrize(
  prize: number,
  mainHits: number,
  bonusHit: boolean,
  mainCount: number
): MatchOutcome {
  if (mainHits === mainCount && bonusHit) return 'jackpot';
  if (prize > 0) return 'won-small';
  return 'lost';
}
